import { produce } from 'immer';
import {
    DateBin,
    ASSET_ROI_MEASURES,
    AssetRoiDimensions,
    ChartType,
    ROIReportTabs
} from 'app/lib/constants';
import {
    CHECK_EVENT_TABLE_ACCESS,
    CLOSE_ASSET_USAGE_DIALOG,
    OPEN_ASSET_USAGE_DIALOG,
    SET_SELECTED_TABLE_INDEX,
    SET_DATE_FILTER_AND_BIN,
    RESET_FILTERS,
    SELECT_SPARKLINE_WINDOW,
    CHANGE_CHART_TYPE,
    SET_PRIMARY_DIMENSION,
    SET_SECONDARY_DIMENSION,
    SET_MEASURE,
    SET_TABLE_CUBE_WAREHOUSE_FILTER,
    SET_TABLE_CUBE_USER_FILTER,
    SET_TABLE_CUBE_TABLE_FILTER,
    SET_TABLE_CUBE_DATE_FILTER,
    SET_TABLE_CUBE_DATE_FILTER_AND_BIN,
    SET_TABLE_CUBE_ACCESS_TYPE_FILTER,
    COPY_FILTER_VALUES,
    SELECT_CHART_AREA,
    SELECT_DASHBOARD_CHART_AREA,
    SELECT_TAB,
    GET_EVENTS_TABLE_COLUMNS,
    RESET_TABLE_CUBE_FILTERS
} from './types.js';

export const INITIAL_STATE = {
    selectedTable: null,
    isDialogOpen: false,
    isCheckingEventTableAccess: false,
    hasAccessToEventTable: null,
    selectedTableIndex: 0,
    dateFilter: null,
    dateBin: DateBin.DAY,
    selectedSparklineTimeWindow: {},
    selectedDashboardChartArea: [],
    selectedTab: ROIReportTabs.ROI_OVERVIEW,
    eventsTableColumns: null,
    tableCube: {
        primaryDimension: AssetRoiDimensions.DATE,
        secondaryDimension: null,
        selectedMeasure: ASSET_ROI_MEASURES[0],
        chartType: ChartType.LINE_CHART,
        dateFilter: null,
        dateBin: null,
        warehouseFilter: null,
        userFilter: null,
        tableFilter: null,
        accessTypeFilter: null,
        selectedChartArea: []
    }
};

export const ROIReportingReducer = produce((draft, action) => {
    switch (action.type) {
        case SET_SELECTED_TABLE_INDEX:
            draft.selectedTableIndex = action.payload;
            break;
        case OPEN_ASSET_USAGE_DIALOG:
            draft.isDialogOpen = true;
            draft.dateBin = DateBin.WEEKDAY;
            draft.selectedTable = action.payload.table;
            break;
        case CLOSE_ASSET_USAGE_DIALOG:
            draft.isDialogOpen = false;
            draft.selectedTable = null;
            break;
        case CHECK_EVENT_TABLE_ACCESS.STARTED:
            draft.isCheckingEventTableAccess = true;
            break;

        case CHECK_EVENT_TABLE_ACCESS.SUCCEEDED:
            draft.hasAccessToEventTable = true;
            draft.isCheckingEventTableAccess = false;
            break;
        case CHECK_EVENT_TABLE_ACCESS.FAILED:
            draft.hasAccessToEventTable = false;
            draft.isCheckingEventTableAccess = false;
            break;
        case SET_DATE_FILTER_AND_BIN: {
            const { rangeFrom, rangeTo, bin } = action.payload;
            draft.dateBin = bin;
            draft.dateFilter = { rangeFrom, rangeTo };
            draft.selectedSparklineTimeWindow = INITIAL_STATE.selectedSparklineTimeWindow;
            break;
        }
        case RESET_FILTERS:
            draft.dateBin = INITIAL_STATE.dateBin;
            draft.dateFilter = null;
            draft.selectedSparklineTimeWindow = INITIAL_STATE.selectedSparklineTimeWindow;
            break;
        case SELECT_SPARKLINE_WINDOW: {
            const { index, chart } = action.payload;
            draft.selectedSparklineTimeWindow[chart] = index;
            break;
        }
        case CHANGE_CHART_TYPE:
            draft.tableCube.chartType = action.payload;
            draft.tableCube.selectedChartArea = [];
            break;
        case SET_PRIMARY_DIMENSION:
            draft.tableCube.primaryDimension = action.payload;
            if (draft.tableCube.secondaryDimension === action.payload) {
                draft.tableCube.secondaryDimension = null;
            }
            if (action.payload !== AssetRoiDimensions.DATE) {
                draft.tableCube.chartType = ChartType.BAR_CHART;
            }
            draft.tableCube.selectedChartArea = [];
            break;
        case SET_SECONDARY_DIMENSION:
            draft.tableCube.secondaryDimension = action.payload;
            break;
        case SET_MEASURE:
            draft.tableCube.selectedMeasure = action.payload;
            break;
        case SET_TABLE_CUBE_DATE_FILTER:
            draft.tableCube.dateFilter = action.payload;
            draft.tableCube.selectedChartArea = [];
            break;
        case SET_TABLE_CUBE_WAREHOUSE_FILTER:
            draft.tableCube.warehouseFilter = action.payload;
            draft.tableCube.selectedChartArea = [];
            break;
        case SET_TABLE_CUBE_USER_FILTER:
            draft.tableCube.userFilter = action.payload;
            draft.tableCube.selectedChartArea = [];
            break;
        case SET_TABLE_CUBE_TABLE_FILTER:
            draft.tableCube.tableFilter = action.payload;
            draft.tableCube.selectedChartArea = [];
            break;
        case SET_TABLE_CUBE_DATE_FILTER_AND_BIN: {
            const { rangeFrom, rangeTo, bin } = action.payload;
            draft.tableCube.dateBin = bin;
            draft.tableCube.dateFilter = { rangeFrom, rangeTo };
            draft.tableCube.selectedChartArea = [];
            break;
        }
        case SET_TABLE_CUBE_ACCESS_TYPE_FILTER:
            draft.tableCube.accessTypeFilter = action.payload;
            draft.tableCube.selectedChartArea = [];
            break;
        case COPY_FILTER_VALUES: {
            const { dateFilter, dateBin, tableFilter } = action.payload;
            draft.tableCube.dateFilter = dateFilter;
            draft.tableCube.dateBin = dateBin;
            draft.tableCube.tableFilter = tableFilter;
            break;
        }
        case SELECT_CHART_AREA:
            draft.tableCube.selectedChartArea = action.payload;
            break;
        case SELECT_DASHBOARD_CHART_AREA:
            draft.selectedDashboardChartArea = action.payload;
            break;
        case SELECT_TAB:
            draft.selectedTab = action.payload.id;
            if (action.payload.id === ROIReportTabs.ROI_OVERVIEW) {
                draft.selectedDashboardChartArea = [];
            }
            break;
        case GET_EVENTS_TABLE_COLUMNS.SUCCEEDED:
            draft.eventsTableColumns = action.payload.columns;
            break;
        case GET_EVENTS_TABLE_COLUMNS.FAILED:
            draft.eventsTableColumns = null;
            break;
        case RESET_TABLE_CUBE_FILTERS:
            draft.tableCube.userFilter = null;
            draft.tableCube.warehouseFilter = null;
            draft.tableCube.dateFilter = null;
            draft.tableCube.tableFilter = null;
            draft.tableCube.accessTypeFilter = null;
            draft.tableCube.selectedChartArea = [];
            break;
        default:
            break;
    }
}, INITIAL_STATE);
